import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { VehicleCardService } from "@/services/vehicleCard/vehicleCardService";
import { ValuationService } from "@/services/valuation/valuationService";
import {
  VehicleCardImages,
  ServiceDetail,
  GetAllVehicleCardsResponse,
  CreateVehicleCardRequest,
  CreateVehicleCardManuallyRequest,
} from "@/types/vehicleCard";
import { VehicleDetails } from "@/types/vehicleDetails";
import { UserDetails } from "@/types/userDetails";
import { PriceInfo, ValueEstimation } from "@/types/vehicleCard";
import { FileObject } from "@/types/shared/file";

export const useVehicleCard = (options?: {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const queryClient = useQueryClient();

  // Get all vehicle cards query with pagination and filters
  const getAllVehicleCardsQuery = useQuery({
    queryKey: ["vehicle-cards", options],
    queryFn: () =>
      VehicleCardService.getAllVehicleCards(
        options?.page || 1,
        options?.limit || 10,
        { search: options?.search, status: options?.status },
      ),
    // Admin data must always reflect the backend (user ruling 2026-09-20):
    // nothing here is ever served from cache.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Get vehicle card with full details
  const useGetVehicleCard = (vehicleCardId: string) =>
    useQuery({
      queryKey: ["vehicle-card", vehicleCardId],
      queryFn: () => VehicleCardService.getVehicleCard(vehicleCardId),
      enabled: !!vehicleCardId,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: "always",
    });

  // Update vehicle card status mutation
  const updateVehicleCardStatusMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      newStatus,
    }: {
      vehicleCardId: string;
      newStatus: string;
    }) => {
      return await VehicleCardService.updateVehicleCardStatus(
        vehicleCardId,
        newStatus,
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch vehicle card data
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Update owner information mutation
  const updateOwnerMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      customerId,
    }: {
      vehicleCardId: string;
      customerId: string;
    }) => {
      return VehicleCardService.updateVehicleCardOwner(
        vehicleCardId,
        customerId,
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch vehicle card data
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Update vehicle details mutation
  const updateVehicleDetailsMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      vehicleDetails,
    }: {
      vehicleCardId: string;
      vehicleDetails: VehicleDetails;
    }) => {
      return await VehicleCardService.updateVehicleCardVehicleDetails(
        vehicleCardId,
        vehicleDetails,
      );
    },
    onSuccess: (data, variables) => {
      // Only invalidate if mutation was successful
      if (data?.success) {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
        });
        queryClient.invalidateQueries({
          queryKey: ["vehicle-cards"],
          exact: false,
        });
      }
    },
    onError: (error: any) => {},
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      comment,
    }: {
      vehicleCardId: string;
      comment: { text: string; author: string; mentions?: string[] };
    }) => {
      return await VehicleCardService.addComment(vehicleCardId, comment);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch vehicle card data
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      commentId,
      text,
    }: {
      vehicleCardId: string;
      commentId: string;
      text: string;
    }) => {
      return await VehicleCardService.updateComment(
        vehicleCardId,
        commentId,
        text,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      commentId,
    }: {
      vehicleCardId: string;
      commentId: string;
    }) => {
      return await VehicleCardService.deleteComment(vehicleCardId, commentId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Fetch rating intervals from AutoUncle (admin)
  const fetchRatingIntervalsMutation = useMutation({
    mutationFn: ({ vehicleCardId }: { vehicleCardId: string }) =>
      ValuationService.fetchVehicleCardRatingIntervals(vehicleCardId),
    onSuccess: (_data, variables) => {
      // The refresh also rewrites the stored headline price (market/purchase price),
      // which is rendered from the card query - not from the mutation result - so the
      // card has to be refetched for it to re-render.
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Create vehicle card from lead mutation
  const createVehicleCardMutation = useMutation({
    mutationFn: async (request: CreateVehicleCardRequest) => {
      return await VehicleCardService.createVehicleCard(request);
    },
    onSuccess: () => {
      // Invalidate and refetch all vehicle cards queries (with any options)
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Create vehicle card manually mutation
  const createVehicleCardManuallyMutation = useMutation({
    mutationFn: async (request: CreateVehicleCardManuallyRequest) => {
      return await VehicleCardService.createVehicleCardManually(request);
    },
    onSuccess: () => {
      // Invalidate and refetch all vehicle cards queries (with any options)
      queryClient.invalidateQueries({
        queryKey: ["vehicle-cards"],
        exact: false,
      });
    },
  });

  // Add vehicle card document mutation
  const addVehicleCardDocumentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      file,
      category,
    }: {
      vehicleCardId: string;
      file: File;
      category: "invoice" | "contract" | "ownerChange" | "other";
    }) => {
      return await VehicleCardService.addVehicleCardDocument(
        vehicleCardId,
        file,
        category,
      );
    },
    onSuccess: (data, variables) => {
      // Optimistically update the cache based on category
      queryClient.setQueryData(
        ["vehicle-card", variables.vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          if (variables.category === "invoice") {
            // If invoice doesn't exist, we need to create it with the file
            if (!oldData.data.payment?.invoice) {
              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  payment: {
                    ...oldData.data.payment,
                    invoice: {
                      id: `invoice_${Date.now()}`,
                      paymentId: oldData.data.payment?.id || "",
                      listingId: "",
                      invoice_number: "",
                      created_at: new Date().toISOString(),
                      invoice_file: data,
                    },
                  },
                },
              };
            }

            // Update existing invoice
            return {
              ...oldData,
              data: {
                ...oldData.data,
                payment: {
                  ...oldData.data.payment,
                  invoice: {
                    ...oldData.data.payment.invoice,
                    invoice_file: data,
                  },
                },
              },
            };
          } else if (variables.category === "contract") {
            // If contract doesn't exist, we need to create it with the file
            if (!oldData.data.payment?.contract) {
              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  payment: {
                    ...oldData.data.payment,
                    contract: {
                      id: `contract_${Date.now()}`,
                      paymentId: oldData.data.payment?.id || "",
                      listingId: "",
                      contract_number: "",
                      created_at: new Date().toISOString(),
                      contract_file: data,
                    },
                  },
                },
              };
            }

            // Update existing contract
            return {
              ...oldData,
              data: {
                ...oldData.data,
                payment: {
                  ...oldData.data.payment,
                  contract: {
                    ...oldData.data.payment.contract,
                    contract_file: data,
                  },
                },
              },
            };
          } else if (variables.category === "ownerChange") {
            return {
              ...oldData,
              data: {
                ...oldData.data,
                documents: {
                  ...oldData.data.documents,
                  owner_change: [
                    ...(oldData.data.documents?.owner_change || []),
                    data,
                  ],
                },
              },
            };
          } else if (variables.category === "other") {
            return {
              ...oldData,
              data: {
                ...oldData.data,
                documents: {
                  ...oldData.data.documents,
                  other_documents: [
                    ...(oldData.data.documents?.other_documents || []),
                    data,
                  ],
                },
              },
            };
          }

          return oldData;
        },
      );

      // Force immediate refetch to ensure documents appear
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
        refetchType: "active",
      });

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  // Remove document mutation
  const removeDocumentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      documentId,
    }: {
      vehicleCardId: string;
      documentId: string;
    }) => {
      return await VehicleCardService.removeVehicleCardDocument(
        vehicleCardId,
        documentId,
      );
    },
    onMutate: async ({ vehicleCardId, documentId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["vehicle-card", vehicleCardId],
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData([
        "vehicle-card",
        vehicleCardId,
      ]);

      // Optimistically update the cache - remove document from all possible locations
      queryClient.setQueryData(
        ["vehicle-card", vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          const updatedData = { ...oldData.data };

          // Remove from owner_change array
          if (updatedData.documents?.owner_change) {
            updatedData.documents.owner_change =
              updatedData.documents.owner_change.filter(
                (doc: any) => doc.id !== documentId,
              );
          }

          // Remove from other_documents array
          if (updatedData.documents?.other_documents) {
            updatedData.documents.other_documents =
              updatedData.documents.other_documents.filter(
                (doc: any) => doc.id !== documentId,
              );
          }

          // Remove from contract if documentId matches
          if (updatedData.payment?.contract?.contract_file?.id === documentId) {
            updatedData.payment = {
              ...updatedData.payment,
              contract: {
                ...updatedData.payment.contract,
                contract_file: undefined,
              },
            };
          }

          // Remove from invoice if documentId matches
          if (updatedData.payment?.invoice?.invoice_file?.id === documentId) {
            updatedData.payment = {
              ...updatedData.payment,
              invoice: {
                ...updatedData.payment.invoice,
                invoice_file: undefined,
              },
            };
          }

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );

      return { previousData };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ["vehicle-card", variables.vehicleCardId],
          context.previousData,
        );
      }
    },
    onSuccess: (data, variables) => {
      // Refetch in background to sync with server (optimistic update already done in onMutate)
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
          refetchType: "active",
        });
      }, 500);

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  // Add vehicle card image mutation
  const addVehicleCardImageMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      category,
      file,
      isThumbnail,
      tireType,
    }: {
      vehicleCardId: string;
      category: "exterior" | "interior" | "damage" | "service" | "tires";
      file: File;
      isThumbnail?: boolean;
      tireType?: "primary" | "secondary";
    }) => {
      return await VehicleCardService.addVehicleCardImage(
        vehicleCardId,
        category,
        file,
        isThumbnail,
        tireType,
      );
    },
    onSuccess: (data, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        ["vehicle-card", variables.vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          const updatedData = { ...oldData.data };

          // Add image to appropriate section
          if (variables.category === "exterior") {
            updatedData.images = {
              ...updatedData.images,
              exterior: [
                ...(updatedData.images?.exterior || []),
                {
                  ...data,
                  isThumbnail: variables.isThumbnail || false,
                },
              ],
            };
          } else if (variables.category === "interior") {
            updatedData.images = {
              ...updatedData.images,
              interior: [...(updatedData.images?.interior || []), data],
            };
          } else if (variables.category === "damage") {
            updatedData.images = {
              ...updatedData.images,
              damage: [
                ...(updatedData.images?.damage || []),
                {
                  ...data,
                  comment: "",
                },
              ],
            };
          } else if (variables.category === "service") {
            updatedData.images = {
              ...updatedData.images,
              serviceImages: [
                ...(updatedData.images?.serviceImages || []),
                data,
              ],
            };
          } else if (variables.category === "tires") {
            const tireType = variables.tireType || "primary";
            updatedData.images = {
              ...updatedData.images,
              tires: {
                ...updatedData.images?.tires,
                [tireType]: [
                  ...(updatedData.images?.tires?.[tireType] || []),
                  data,
                ],
              },
            };
          }

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );

      // Don't refetch immediately - optimistic update already done
      // Only invalidate (without refetch) to mark as stale
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
        refetchType: "none", // Don't refetch immediately
      });

      // Refetch in background after a long delay to sync with server (user won't notice)
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
          refetchType: "active",
        });
      }, 5000); // Much longer delay - user won't notice

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  // Add vehicle card service detail mutation
  const addVehicleCardServiceDetailMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      serviceDetail,
    }: {
      vehicleCardId: string;
      serviceDetail: ServiceDetail;
    }) => {
      return await VehicleCardService.addVehicleCardServiceDetail(
        vehicleCardId,
        serviceDetail,
      );
    },
    onSuccess: (data, variables) => {
      // Update cache with actual data from backend (replaces temp ID with real ID)
      queryClient.setQueryData(
        ["vehicle-card", variables.vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          const updatedData = { ...oldData.data };
          const existingServiceDetails =
            updatedData.images?.serviceDetails?.serviceDetails || [];

          // Find and replace temp service detail with actual one from backend
          const tempId = variables.serviceDetail.id;
          const updatedServiceDetails = existingServiceDetails
            .filter((sd: any) => sd.id !== tempId) // Remove temp one
            .concat(data); // Add actual one from backend

          updatedData.images = {
            ...updatedData.images,
            serviceDetails: {
              serviceDetails: updatedServiceDetails,
            },
          };

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );

      // Force immediate refetch to ensure service details appear
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
        refetchType: "active",
      });

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
    onError: (error, variables) => {
      // Rollback optimistic update on error
      queryClient.setQueryData(
        ["vehicle-card", variables.vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          const updatedData = { ...oldData.data };
          const existingServiceDetails =
            updatedData.images?.serviceDetails?.serviceDetails || [];
          const tempId = variables.serviceDetail.id;
          const updatedServiceDetails = existingServiceDetails.filter(
            (sd: any) => sd.id !== tempId,
          );

          updatedData.images = {
            ...updatedData.images,
            serviceDetails: {
              serviceDetails: updatedServiceDetails,
            },
          };

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );
    },
  });

  // Remove vehicle card service detail mutation
  const removeVehicleCardServiceDetailMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      serviceDetailId,
    }: {
      vehicleCardId: string;
      serviceDetailId: string;
    }) => {
      return await VehicleCardService.removeVehicleCardServiceDetail(
        vehicleCardId,
        serviceDetailId,
      );
    },
    onMutate: async ({ vehicleCardId, serviceDetailId }) => {
      await queryClient.cancelQueries({
        queryKey: ["vehicle-card", vehicleCardId],
      });
      const previousData = queryClient.getQueryData([
        "vehicle-card",
        vehicleCardId,
      ]);

      // Optimistically remove service detail
      queryClient.setQueryData(
        ["vehicle-card", vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data?.images?.serviceDetails) return oldData;

          const updatedData = { ...oldData.data };
          updatedData.images = {
            ...updatedData.images,
            serviceDetails: {
              serviceDetails:
                updatedData.images.serviceDetails.serviceDetails.filter(
                  (detail: any) => detail.id !== serviceDetailId,
                ),
            },
          };

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["vehicle-card", variables.vehicleCardId],
          context.previousData,
        );
      }
    },
    onSuccess: (data, variables) => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
          refetchType: "active",
        });
      }, 500);

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  // Update vehicle card damage image comment mutation
  const updateVehicleCardDamageImageCommentMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      imageId,
      comment,
    }: {
      vehicleCardId: string;
      imageId: string;
      comment: string;
    }) => {
      return await VehicleCardService.updateVehicleCardDamageImageComment(
        vehicleCardId,
        imageId,
        comment,
      );
    },
    onSuccess: (data, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData(
        ["vehicle-card", variables.vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data?.images?.damage) return oldData;

          const updatedData = { ...oldData.data };
          updatedData.images = {
            ...updatedData.images,
            damage: updatedData.images.damage.map((img: any) =>
              img.id === variables.imageId
                ? { ...img, comment: variables.comment }
                : img,
            ),
          };

          return {
            ...oldData,
            data: updatedData,
          };
        },
      );

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
        refetchType: "none",
      });

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
          refetchType: "active",
        });
      }, 1000);

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  // Remove vehicle card image mutation
  const removeVehicleCardImageMutation = useMutation({
    mutationFn: async ({
      vehicleCardId,
      imageId,
    }: {
      vehicleCardId: string;
      imageId: string;
    }) => {
      return await VehicleCardService.removeVehicleCardImage(
        vehicleCardId,
        imageId,
      );
    },
    onMutate: async ({ vehicleCardId, imageId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["vehicle-card", vehicleCardId],
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData([
        "vehicle-card",
        vehicleCardId,
      ]);

      // Optimistically remove image from cache
      queryClient.setQueryData(
        ["vehicle-card", vehicleCardId],
        (oldData: any) => {
          if (!oldData?.data?.images) return oldData;

          const updatedImages = { ...oldData.data.images };

          // Remove from all possible locations
          if (updatedImages.exterior) {
            updatedImages.exterior = updatedImages.exterior.filter(
              (img: any) => img.id !== imageId,
            );
          }
          if (updatedImages.interior) {
            updatedImages.interior = updatedImages.interior.filter(
              (img: any) => img.id !== imageId,
            );
          }
          if (updatedImages.damage) {
            updatedImages.damage = updatedImages.damage.filter(
              (img: any) => img.id !== imageId,
            );
          }
          if (updatedImages.serviceImages) {
            updatedImages.serviceImages = updatedImages.serviceImages.filter(
              (img: any) => img.id !== imageId,
            );
          }
          if (updatedImages.tires?.primary) {
            updatedImages.tires.primary = updatedImages.tires.primary.filter(
              (img: any) => img.id !== imageId,
            );
          }
          if (updatedImages.tires?.secondary) {
            updatedImages.tires.secondary =
              updatedImages.tires.secondary.filter(
                (img: any) => img.id !== imageId,
              );
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              images: updatedImages,
            },
          };
        },
      );

      return { previousData };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          ["vehicle-card", variables.vehicleCardId],
          context.previousData,
        );
      }
    },
    onSuccess: (data, variables) => {
      // Don't refetch immediately - optimistic update already done
      // Only invalidate (without refetch) to mark as stale
      queryClient.invalidateQueries({
        queryKey: ["vehicle-card", variables.vehicleCardId],
        refetchType: "none", // Don't refetch immediately
      });

      // Refetch in background after a long delay to sync with server (user won't notice)
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["vehicle-card", variables.vehicleCardId],
          refetchType: "active",
        });
      }, 5000); // Much longer delay - user won't notice

      queryClient.invalidateQueries({ queryKey: ["vehicle-cards"] });
    },
  });

  return {
    // Get all vehicle cards with pagination
    vehicleCards: getAllVehicleCardsQuery.data?.data || [],
    totalVehicleCards: getAllVehicleCardsQuery.data?.pagination?.total || 0,
    currentPage: getAllVehicleCardsQuery.data?.pagination?.page || 1,
    totalPages: getAllVehicleCardsQuery.data?.pagination?.totalPages || 1,
    itemsPerPage: getAllVehicleCardsQuery.data?.pagination?.limit || 10,
    isLoadingVehicleCards: getAllVehicleCardsQuery.isLoading,
    isSuccessVehicleCards: getAllVehicleCardsQuery.isSuccess,
    vehicleCardsError: getAllVehicleCardsQuery.error,
    refetchVehicleCards: getAllVehicleCardsQuery.refetch,

    // Get vehicle card with full details
    getVehicleCard: useGetVehicleCard,

    // Update vehicle card status
    updateVehicleCardStatusAsync: updateVehicleCardStatusMutation.mutateAsync,
    isUpdatingVehicleCardStatus: updateVehicleCardStatusMutation.isPending,
    updateVehicleCardStatusError: updateVehicleCardStatusMutation.error,

    // Update owner information
    updateOwnerMutation,
    updateOwnerAsync: updateOwnerMutation.mutateAsync,
    isUpdatingOwner: updateOwnerMutation.isPending,
    updateOwnerError: updateOwnerMutation.error,

    // Update vehicle details
    updateVehicleDetailsMutation,
    updateVehicleDetailsAsync: updateVehicleDetailsMutation.mutateAsync,
    isUpdatingVehicleDetails: updateVehicleDetailsMutation.isPending,
    updateVehicleDetailsError: updateVehicleDetailsMutation.error,

    // Add comment
    addCommentMutation,
    addCommentAsync: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
    addCommentError: addCommentMutation.error,

    updateCommentAsync: updateCommentMutation.mutateAsync,
    isUpdatingComment: updateCommentMutation.isPending,
    updateCommentError: updateCommentMutation.error,

    deleteCommentAsync: deleteCommentMutation.mutateAsync,
    isDeletingComment: deleteCommentMutation.isPending,
    deleteCommentError: deleteCommentMutation.error,

    // Fetch rating intervals
    fetchRatingIntervalsMutation,
    fetchRatingIntervalsAsync: fetchRatingIntervalsMutation.mutateAsync,
    isFetchingRatingIntervals: fetchRatingIntervalsMutation.isPending,
    fetchRatingIntervalsError: fetchRatingIntervalsMutation.error,

    // Create vehicle card
    createVehicleCardMutation,
    createVehicleCard: createVehicleCardMutation.mutateAsync,
    isCreatingVehicleCard: createVehicleCardMutation.isPending,
    createVehicleCardError: createVehicleCardMutation.error,
    createVehicleCardSuccess: createVehicleCardMutation.isSuccess,

    // Create vehicle card manually
    createVehicleCardManuallyMutation,
    createVehicleCardManuallyAsync:
      createVehicleCardManuallyMutation.mutateAsync,
    isCreatingVehicleCardManually: createVehicleCardManuallyMutation.isPending,
    createVehicleCardManuallyError: createVehicleCardManuallyMutation.error,
    createVehicleCardManuallySuccess:
      createVehicleCardManuallyMutation.isSuccess,

    // Add vehicle card document
    addVehicleCardDocument: addVehicleCardDocumentMutation.mutateAsync,
    isAddingVehicleCardDocument: addVehicleCardDocumentMutation.isPending,
    addVehicleCardDocumentError: addVehicleCardDocumentMutation.error,

    // Remove document
    removeDocument: removeDocumentMutation.mutateAsync,
    isRemovingDocument: removeDocumentMutation.isPending,
    removeDocumentError: removeDocumentMutation.error,

    // Add vehicle card image
    addVehicleCardImage: addVehicleCardImageMutation.mutateAsync,
    isAddingVehicleCardImage: addVehicleCardImageMutation.isPending,
    addVehicleCardImageError: addVehicleCardImageMutation.error,

    // Remove vehicle card image
    removeVehicleCardImage: removeVehicleCardImageMutation.mutateAsync,
    isRemovingVehicleCardImage: removeVehicleCardImageMutation.isPending,
    removeVehicleCardImageError: removeVehicleCardImageMutation.error,

    // Add vehicle card service detail
    addVehicleCardServiceDetail:
      addVehicleCardServiceDetailMutation.mutateAsync,
    isAddingVehicleCardServiceDetail:
      addVehicleCardServiceDetailMutation.isPending,
    addVehicleCardServiceDetailError: addVehicleCardServiceDetailMutation.error,

    // Remove vehicle card service detail
    removeVehicleCardServiceDetail:
      removeVehicleCardServiceDetailMutation.mutateAsync,
    isRemovingVehicleCardServiceDetail:
      removeVehicleCardServiceDetailMutation.isPending,
    removeVehicleCardServiceDetailError:
      removeVehicleCardServiceDetailMutation.error,

    // Update vehicle card damage image comment
    updateVehicleCardDamageImageComment:
      updateVehicleCardDamageImageCommentMutation.mutateAsync,
    isUpdatingVehicleCardDamageImageComment:
      updateVehicleCardDamageImageCommentMutation.isPending,
    updateVehicleCardDamageImageCommentError:
      updateVehicleCardDamageImageCommentMutation.error,
  };
};
